import type { UILanguageCode } from "@/locales";

export type QnA = {
    [key in Omit<UILanguageCode, 'fr'> as string]: {
        title: string;
        questions: { question: string; answer: string; }[];
    };
};

export const main: QnA = {
    en: {
        title: 'General',
        questions: [
            {
                question: "What is Monábid?",
                answer: "Monábid is a digital auction platform connecting sellers, collectors, and buyers from around the world. We offer a professional, secure, and transparent bidding and selling experience.",
            },
            {
                question: "Who can use the platform?",
                answer: "Anyone can browse and view auctions. However, to place a bid or sell an item, you must register and create a verified account."
            },
            {
                question: "Does Monábid operate internationally?",
                answer: "Yes. The platform facilitates transactions between buyers and sellers from various countries and supports international shipping."
            }
        ]
    },
    he: {
        title: 'כללי',
        questions: [
            {
                question: "מה זה Monábid?",
                answer: "Monábid היא פלטפורמת מכירות פומביות דיגיטלית, המחברת בין מוכרים, אספנים וקונים מכל העולם, ומציעה חוויית מכירה והצעה מקצועית, מאובטחת ושקופה."
            },
            {
                question: "מי יכול להשתמש בפלטפורמה?",
                answer: "כל אחד יכול לצפות במכירות. לצורך הצעת מחיר או מכירה נדרש רישום ופתיחת חשבון מאומת."
            },
            {
                question: "האם Monábid פועלת גם בזירה הבינלאומית?",
                answer: "כן. הפלטפורמה פועלת מול קונים ומוכרים ממדינות שונות ותומכת במכירות בינלאומיות."
            }
        ]
    },
};

export const bidding: QnA = {
    en: {
        title: 'Bidding',
        questions: [
            {
                question: "How do I place a bid?",
                answer: "After logging into your account, select the desired item and enter your bid amount. The bid must be higher than the current price and meet the required \"Bid Increment.\""
            },
            {
                question: "Can I cancel or change a bid?",
                answer: "No. A bid in an auction is legally binding. We strongly recommend reviewing the product details and shipping terms carefully before placing a bid."
            },
            {
                question: "What happens if I win?",
                answer: "Upon winning, you will receive a confirmation notification with payment and delivery details. Payment must be completed within the specified timeframe."
            },
            {
                question: "What is a Reserve Price?",
                answer: "Some items have a minimum price set by the seller. If this price is not met, the item will not be sold."
            },
            {
                question: "What is \"Proxy Bidding\" (Automatic Bidding)?",
                answer: "Our system allows you to enter the maximum price you are willing to pay. The system will then automatically increase your bid by the minimum increment only if someone else outbids you, up to your set limit."
            },
            {
                question: "What happens if someone bids at the very last second?",
                answer: "To prevent \"Sniping,\" we use a time extension mechanism (\"Soft Close\"). If a bid is placed in the final 2 minutes of the auction, the countdown timer will reset to 2 minutes, ensuring everyone has a fair chance to react."
            }
        ]
    },
    he: {
        title: 'הצעת מחיר (Bidding)',
        questions: [
            {
                question: "איך מציעים הצעה?",
                answer: "לאחר התחברות לחשבון, בחרו בפריט הרצוי והזינו את סכום ההצעה. ההצעה חייבת להיות גבוהה מהמחיר הנוכחי ובהתאם למדרגות ההצעה שנקבעו."
            },
            {
                question: "האם ניתן לבטל או לשנות הצעה?",
                answer: "לא. הצעה במכירה פומבית היא מחייבת מבחינה משפטית. אנחנו ממליצים לבדוק היטב את פרטי המוצר ותנאי המשלוח לפני הגשת ההצעה."
            },
            {
                question: "מה קורה אם זכיתי במכירה?",
                answer: "לאחר הזכייה תישלח אליכם הודעת אישור עם פרטי תשלום ואספקה. יש להשלים את התשלום במסגרת הזמן שנקבעה."
            },
            {
                question: "מהו מחיר רזרבה?",
                answer: "בחלק מהפריטים מוגדר מחיר מינימום על ידי המוכר. אם מחיר זה לא הושג – הפריט לא יימכר."
            },
            {
                question: "מה זה \"הצעה אוטומטית\" (Proxy Bidding)?",
                answer: "המערכת מאפשרת לך להזין את המחיר המקסימלי שאת מוכנה לשלם. המערכת תעלה את ההצעה עבורך באופן אוטומטי בצעדים מינימליים, רק אם מישהו אחר הגיש הצעה גבוהה יותר, ועד לתקרה שקבעת."
            },
            {
                question: "מה קורה אם מישהו מציע הצעה בשנייה האחרונה?",
                answer: "כדי למנוע \"חטיפות\" (Sniping), אנחנו משתמשים במנגנון הארכת זמן. אם תוגש הצעה ב-2 הדקות האחרונות של המכירה, שעון הספירה לאחור יתאפס ל-2 דקות נוספות כדי לאפשר לכולם הזדמנות הוגנת להגיב."
            }
        ]
    },
};

export const selling: QnA = {
    en: {
        title: 'Selling',
        questions: [
            {
                question: "How can I sell on Monábid?",
                answer: "Go to the \"Sell with Us\" page and submit an application with the item details. The Monábid team will review your request and guide you through the process."
            },
            {
                question: "What items can be sold?",
                answer: "Monábid specializes in select items such as: collectibles, art, Judaica, rare books and manuscripts, historical documents, jewelry, and unique valuable objects."
            },
            {
                question: "Is there a selling commission?",
                answer: "Yes. The commission rate varies depending on the item type and the specific auction. It will be clearly displayed in advance."
            },
            {
                question: "Can I get a valuation before selling?",
                answer: "Absolutely. You can submit a request for an estimate and receive a professional evaluation before listing the item."
            }
        ]
    },
    he: {
        title: 'מכירה',
        questions: [
            {
                question: "איך ניתן למכור ב-Monábid?",
                answer: "יש להיכנס לעמוד \"למכור איתנו\" ולהגיש בקשה הכוללת פרטי הפריט. צוות Monábid יבחן את הבקשה וילווה אתכם בתהליך."
            },
            {
                question: "אילו פריטים ניתן למכור?",
                answer: "Monábid מתמחה בפריטים נבחרים כגון: פריטי אספנות, אמנות, יודאיקה, ספרים וכתבי יד נדירים, מסמכים היסטוריים, תכשיטים וחפצים ייחודיים בעלי ערך."
            },
            {
                question: "האם יש עמלת מכירה?",
                answer: "כן. גובה העמלה משתנה בהתאם לסוג הפריט ולמכירה, ויוצג בצורה ברורה מראש."
            },
            {
                question: "האם ניתן לקבל הערכת שווי לפני המכירה?",
                answer: "בהחלט. ניתן להגיש בקשת הערכה ולקבל אומדן מקצועי טרם העלאת הפריט למכירה."
            }
        ]
    }
}

export const paymentsAndFees: QnA = {
    en: {
        title: 'Payments & Fees',
        questions: [
            {
                question: "What payment methods are accepted?",
                answer: "We accept credit cards and other secure payment methods, depending on the user's location."
            },
            {
                question: "Is there a Buyer's Premium?",
                answer: "In some auctions, a Buyer's Premium applies. This fee is clearly displayed on the item page before you place a bid."
            },
            {
                question: "Is the payment secure?",
                answer: "Yes. Monábid uses advanced security measures and industry-standard encryption protocols."
            }
        ]
    },
    he: {
        title: 'תשלומים ועמלות',
        questions: [
            {
                question: "אילו אמצעי תשלום מתקבלים?",
                answer: "אנו מקבלים כרטיסי אשראי ואמצעי תשלום מאובטחים נוספים, בהתאם למדינת המשתמש."
            },
            {
                question: "האם קיימת עמלת קונה?",
                answer: "בחלק מהמכירות קיימת עמלת קונה. העמלה מוצגת באופן ברור בדף הפריט לפני הגשת ההצעה."
            },
            {
                question: "האם התשלום מאובטח?",
                answer: "כן. Monábid משתמשת באמצעי אבטחה מתקדמים ותקני הצפנה מקובלים בתעשייה."
            }
        ]
    }
}

export const shippingAndDelivery: QnA = {
    en: {
        title: 'Shipping & Delivery',
        questions: [
            {
                question: "Who is responsible for shipping?",
                answer: "Shipping terms vary from one auction house to another and from sale to sale. Specific details appear on each item page."
            },
            {
                question: "Do you offer international shipping?",
                answer: "In most cases, yes, subject to local regulations and the type of item."
            }
        ]
    },
    he: {
        title: 'משלוחים ואספקה',
        questions: [
            {
                question: "מי אחראי על המשלוח?",
                answer: "תנאי המשלוח משתנים מבית מכירות אחד למשנהו, ומכירה למכירה ומופיעים בדף הפריט."
            },
            {
                question: "האם יש משלוחים בינלאומיים?",
                answer: "ברוב המקרים כן, בכפוף לתקנות מקומיות ולסוג הפריט."
            }
        ]
    }
}

export const trustAndSafety: QnA = {
    en: {
        title: 'Trust & Safety',
        questions: [
            {
                question: "How do I know the seller is reliable?",
                answer: "Every seller has a rating and review system based on previous buyers. We always recommend checking the seller's history before placing a high bid."
            },
            {
                question: "What happens if the item doesn't arrive or doesn't match the description?",
                answer: "The platform offers Buyer Protection. If there is an issue, you can open a ticket with customer support within 48 hours of receiving the item. We will mediate to resolve the issue or issue a refund if necessary, in accordance with the user agreement and legal terms."
            }
        ]
    },
    he: {
        title: 'אמינות ואבטחה',
        questions: [
            {
                question: "איך אני יודע שהמוכר אמין?",
                answer: "לכל מוכר יש מערכת דירוגים וביקורות מקונים קודמים. אנחנו ממליצים תמיד לבדוק את היסטוריית המכירות של המוכר לפני הגשת הצעה גבוהה."
            },
            {
                question: "מה קורה אם המוצר לא הגיע או לא תואם לתיאור?",
                answer: "הפלטפורמה מציעה הגנת קונה. אם ישנה בעיה, ניתן לפתוח פנייה לשירות הלקוחות תוך 48 שעות מקבלת המוצר, ואנחנו נתווך בפתרון הבעיה או נבצע החזר כספי במידת הצורך. בהתאם להסכם המשתמש ולתנאים המשפטיים."
            }
        ]
    }
}

export const accountAndSupport: QnA = {
    en: {
        title: 'Account & Support',
        questions: [
            {
                question: "Do I need an account to view auctions?",
                answer: "No. Viewing is open to everyone. Registration is only required to bid or sell."
            },
            {
                question: "How can I contact support?",
                answer: "You can reach us via Chat, WhatsApp, or through the \"Contact Us\" form."
            }
        ]
    },
    he: {
        title: 'חשבון ותמיכה',
        questions: [
            {
                question: "האם חייבים לפתוח חשבון כדי לצפות במכירות?",
                answer: "לא. צפייה פתוחה לכולם. רישום נדרש לצורך הצעת מחיר או מכירה."
            },
            {
                question: "איך יוצרים קשר עם התמיכה?",
                answer: "ניתן לפנות אלינו בצ'אט, בוואטסאפ או באמצעות טופס יצירת קשר."
            }
        ]
    }
}

export const legal: QnA = {
    en: {
        title: 'Legal',
        questions: [
            {
                question: "Where can I find the User Agreement and legal terms?",
                answer: "All legal documents are available in the \"Learn More\" section of the website."
            }
        ]
    },
    he: {
        title: 'משפטי',
        questions: [
            {
                question: "איפה ניתן למצוא את ההסכם והתנאים המשפטיים?",
                answer: "כל המסמכים המשפטיים זמינים באזור \"למד עוד\" באתר."
            }
        ]
    }
}
